import { z } from "zod";
export const businessRoles = [
    "chief_of_staff",
    "analyst",
    "analyst_deputy",
    "administrator",
    "ceo_md",
    "board_member",
    "champion",
    "champion_deputy",
    "department_head",
];
export const businessRoleLabels = {
    chief_of_staff: "Chief of Staff",
    analyst: "S&E Analyst",
    analyst_deputy: "S&E Analyst Deputy (retired — reassign)",
    administrator: "Administrator",
    ceo_md: "CEO / MD",
    board_member: "Board Member",
    champion: "Department Champion",
    champion_deputy: "Department Champion Deputy",
    department_head: "Department Head",
};
export const businessCapabilities = [
    "submit",
    "edit",
    "score.view",
    "score.edit",
    "decide",
    "delivery.update",
    "governance.view",
    "governance.manage",
    "export",
    "system.manage",
];
export const departmentalRoles = new Set([
    "champion",
    "champion_deputy",
    "department_head",
]);
const analyst = [
    "submit",
    "edit",
    "score.view",
    "score.edit",
    "decide",
    "delivery.update",
    "governance.view",
    "governance.manage",
];
const executive = ["score.view", "governance.view"];
export const defaultBusinessGrants = {
    chief_of_staff: [...analyst, "export", "system.manage"],
    ceo_md: [...executive],
    board_member: [...executive],
    analyst: [...analyst, "export", "system.manage"],
    analyst_deputy: [...analyst],
    administrator: ["system.manage"],
    champion: ["submit", "edit", "delivery.update"],
    champion_deputy: ["submit", "edit", "delivery.update"],
    department_head: ["score.view"],
};
export const businessAssignmentSchema = z
    .object({
    email: z
        .string()
        .trim()
        .email()
        .transform((s) => s.toLowerCase()),
    roles: z
        .array(z.enum(businessRoles))
        .min(1)
        .max(businessRoles.length)
        .refine((v) => new Set(v).size === v.length, "Duplicate roles"),
    departments: z
        .array(z.string().trim().min(2).max(120))
        .max(50)
        .refine((v) => new Set(v.map((s) => s.toLowerCase())).size === v.length, "Duplicate departments"),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.roles.some((r) => departmentalRoles.has(r)) && !v.departments.length)
        ctx.addIssue({
            code: "custom",
            path: ["departments"],
            message: "Department roles require at least one assigned department",
        });
});
const grantSchema = z
    .array(z.enum(businessCapabilities))
    .max(businessCapabilities.length)
    .refine((v) => new Set(v).size === v.length, "Duplicate capabilities");
export const businessPolicySchema = z
    .object({
    version: z.number().int().nonnegative(),
    requireAssignments: z.boolean().optional(),
    roles: z
        .object({
        chief_of_staff: grantSchema,
        analyst: grantSchema,
        analyst_deputy: grantSchema,
        administrator: grantSchema,
        ceo_md: grantSchema,
        board_member: grantSchema,
        champion: grantSchema,
        champion_deputy: grantSchema,
        department_head: grantSchema,
    })
        .strict(),
    assignments: z
        .array(businessAssignmentSchema)
        .max(1000)
        .refine((v) => new Set(v.map((a) => a.email)).size === v.length, "Duplicate accounts"),
})
    .strict()
    .superRefine((v, ctx) => {
    for (const role of businessRoles) {
        const grants = v.roles[role];
        if (grants.includes("score.edit") && !grants.includes("score.view"))
            ctx.addIssue({
                code: "custom",
                path: ["roles", role],
                message: "Score editing requires score viewing",
            });
        if (grants.includes("governance.manage") &&
            !grants.includes("governance.view"))
            ctx.addIssue({
                code: "custom",
                path: ["roles", role],
                message: "Governance management requires viewing",
            });
        if (!["administrator", "chief_of_staff", "analyst"].includes(role) && grants.includes("system.manage"))
            ctx.addIssue({
                code: "custom",
                path: ["roles", role],
                message: "System management is limited to Administrator, Chief of Staff and S&E Analyst",
            });
        if (role === "administrator" && grants.some((g) => g !== "system.manage"))
            ctx.addIssue({
                code: "custom",
                path: ["roles", role],
                message: "Assign a business role separately from Administrator",
            });
        if (departmentalRoles.has(role) &&
            grants.some((g) => [
                "score.edit",
                "decide",
                "governance.view",
                "governance.manage",
                "export",
            ].includes(g)))
            ctx.addIssue({
                code: "custom",
                path: ["roles", role],
                message: "Department roles cannot administer scoring, approvals, governance or exports",
            });
    }
});
export function resolveBusinessAccess(policy, email) {
    const assignment = policy.assignments.find((a) => a.email === email.trim().toLowerCase());
    if (!assignment)
        return undefined;
    const business = assignment.roles.filter((r) => r !== "administrator");
    return {
        ...assignment,
        scope: business.some((r) => !departmentalRoles.has(r))
            ? "all"
            : business.length
                ? "department"
                : "none",
        capabilities: [
            ...new Set(assignment.roles.flatMap((r) => policy.roles[r])),
        ],
    };
}
export function departmentAllowed(access, team) {
    return (access.scope === "all" ||
        (access.scope === "department" &&
            access.departments.some((d) => d.toLowerCase() === team.trim().toLowerCase())));
}
